import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle2, Loader2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useCurrency } from '@/hooks/useCurrency';
import type { SafetyStockApplyModalMode } from '../hooks/useSafetyStockApplyFlow';
import type { SafetyStockRunStatus } from '@/services/safetyStockService';

interface Props {
  open: boolean;
  mode: SafetyStockApplyModalMode;
  isRunning: boolean;
  flowComplete: boolean;
  errorMessage: string | null;
  runStatus: SafetyStockRunStatus | null;
  reattached: boolean;
  confirmItemCount: number | null;
  onConfirm: () => void;
  onClose: () => void;
  onRetry: () => void;
}

export function SafetyStockApplyProgressModal({
  open,
  mode,
  isRunning,
  flowComplete,
  errorMessage,
  runStatus,
  reattached,
  confirmItemCount,
  onConfirm,
  onClose,
  onRetry,
}: Props) {
  const { t } = useTranslation();
  const { symbol } = useCurrency();
  const navigate = useNavigate();

  if (!open) return null;

  const total = runStatus?.total_items ?? confirmItemCount ?? 0;
  const done = (runStatus?.successful_items ?? 0) + (runStatus?.failed_items ?? 0);
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;

  const content = (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[1px]"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="safety-stock-apply-modal-title"
        className="relative w-full max-w-md rounded-xl border border-gray-200 bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h2
              id="safety-stock-apply-modal-title"
              className="text-base font-semibold text-gray-900"
            >
              {mode === 'confirm'
                ? t('safetyStock.apply.confirmTitle')
                : flowComplete
                  ? t('safetyStock.apply.completedTitle')
                  : errorMessage
                    ? t('safetyStock.apply.errorTitle')
                    : t('safetyStock.apply.progressTitle')}
            </h2>
            {mode === 'confirm' ? (
              <p className="mt-1 text-sm text-gray-600">
                {t('safetyStock.apply.confirmMessage', {
                  count: typeof confirmItemCount === 'number' ? confirmItemCount : 0,
                })}
              </p>
            ) : (
              <p className="mt-1 text-sm text-gray-500">
                {reattached
                  ? t('safetyStock.apply.reattachHint')
                  : isRunning
                    ? t('safetyStock.apply.progressHintDismissible')
                    : t('safetyStock.apply.progressHint')}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label={t('safetyStock.apply.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {mode === 'confirm' ? (
          <div className="space-y-3 px-5 py-4">
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {t('safetyStock.apply.persistWarning')}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                onClick={onClose}
              >
                {t('safetyStock.apply.cancel')}
              </button>
              <button
                type="button"
                className="rounded-md bg-stocker-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90"
                onClick={() => void onConfirm()}
              >
                {t('safetyStock.apply.confirmCta')}
              </button>
            </div>
          </div>
        ) : (
          <div className="px-5 py-4">
            <div className="mb-3 flex items-center justify-between text-sm text-gray-700">
              <span className="inline-flex items-center gap-2 font-medium">
                {isRunning ? (
                  <Loader2 className="h-4 w-4 animate-spin text-stocker-primary" />
                ) : flowComplete ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                ) : errorMessage ? (
                  <AlertCircle className="h-4 w-4 text-red-600" />
                ) : null}
                {runStatus?.status || '…'}
              </span>
              <span className="tabular-nums text-gray-500">
                {done} / {total || '—'} ({pct}%)
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-gray-100">
              <div
                className="h-full rounded-full bg-stocker-primary transition-all duration-300"
                style={{ width: `${pct}%` }}
              />
            </div>
            {runStatus ? (
              <p className="mt-2 text-xs text-gray-500">
                {t('safetyStock.apply.progressDetail', {
                  ok: runStatus.successful_items,
                  fail: runStatus.failed_items,
                })}
              </p>
            ) : null}

            {errorMessage ? (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                {errorMessage}
              </div>
            ) : null}

            {flowComplete ? (
              <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                {t('safetyStock.apply.completedMessage')}
                {runStatus?.totals ? (
                  <span className="mt-1 block text-xs">
                    {t('safetyStock.apply.completedTotals', {
                      units: runStatus.totals.safety_stock_units,
                      value: runStatus.totals.safety_stock_value,
                      currencySymbol: symbol,
                    })}
                  </span>
                ) : null}
              </div>
            ) : null}

            <div className="mt-5 flex flex-wrap justify-end gap-2">
              {errorMessage && !isRunning ? (
                <button
                  type="button"
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                  onClick={() => void onRetry()}
                >
                  {t('safetyStock.apply.retry')}
                </button>
              ) : null}
              {flowComplete ? (
                <button
                  type="button"
                  className="rounded-md bg-stocker-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90"
                  onClick={() => {
                    onClose();
                    navigate('/safety-stock/resumen');
                  }}
                >
                  {t('safetyStock.apply.viewImpact')}
                </button>
              ) : null}
              {isRunning ? (
                <button
                  type="button"
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                  onClick={onClose}
                >
                  {t('safetyStock.apply.continueInBackground')}
                </button>
              ) : null}
              {!isRunning && (flowComplete || errorMessage) ? (
                <button
                  type="button"
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                  onClick={onClose}
                >
                  {t('safetyStock.apply.close')}
                </button>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
